import React, { useEffect, useState } from 'react';
import { BOAPI } from '../service/UsersService';
import utils from '../utils/utils';

const fullUrl = (p) => (!p ? null : /^https?:\/\//i.test(p) ? p : `${utils.BaseUrl}${p}`);

// אייקונים לבחירה (שמות Ionicons - כך הם מוצגים באפליקציה)
const ICON_OPTIONS = [
  ['', 'ללא אייקון'], ['star-outline', 'כוכב'], ['sparkles-outline', 'נצנוץ'], ['flame-outline', 'להבה'],
  ['heart-outline', 'לב'], ['ribbon-outline', 'סרט'], ['trophy-outline', 'גביע'], ['gift-outline', 'מתנה'],
  ['location-outline', 'מיקום'], ['time-outline', 'שעון'], ['pricetag-outline', 'תגית'],
];
const MODE_LABEL = {
  auto_new: 'אוטומטית - העסקים החדשים ביותר (עם הוספה והסרה ידנית)',
  manual: 'ידנית - רק העסקים שבוחרים כאן',
};

// טאב "קרוסלות בעמוד הראשי" - שליטה בקרוסלות בתי העסק שמוצגות בפיד באפליקציה
// ("הצטרפו לאחרונה", "המומלצים" וכו'): כותרת, אייקון, כמה עסקים, אחרי כמה שורות
// בפיד, מאיזה גודל מועדון להציג, ואילו עסקים - הוספה/הסרה/סדר. התצוגה המקדימה
// היא בדיוק מה שהאפליקציה מציגה (fnFeedSectionItems). "מחיקה" = השבתה
const FeedSectionsTab = () => {
  const [sections, setSections] = useState([]);
  const [preview, setPreview] = useState([]);
  const [items, setItems] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [addPick, setAddPick] = useState({});

  const load = async () => {
    try {
      const resp = await BOAPI.fetchFeedSections();
      if (resp?.[0]?.[0]?.code === 0) {
        const secs = resp[1] || [];
        setSections(secs);
        setPreview(resp[2] || []);
        setItems(resp[3] || []);
        setDrafts(Object.fromEntries(secs.map((s) => [s.id, {
          title: s.title, icon: s.icon || '', maxItems: String(s.maxItems), minBusinesses: String(s.minBusinesses),
          afterRows: String(s.afterRows), sortOrder: String(s.sortOrder), active: !!s.active,
        }])));
      } else {
        setError(resp?.[0]?.[0]?.msg || 'שגיאה בטעינת הקרוסלות');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    BOAPI.fetchBusinesses()
      .then((resp) => {
        if (resp?.[0]?.[0]?.code === 0) {
          setBusinesses((resp[1] || []).filter((b) => b.active && b.ownerConfirmedAt).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'he')));
        }
      })
      .catch(() => {});
  }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };
  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  const save = async (s) => {
    const d = drafts[s.id];
    setBusy(`save-${s.id}`);
    setError('');
    try {
      const resp = await BOAPI.saveFeedSection({
        id: s.id, title: d.title, icon: d.icon, maxItems: Number(d.maxItems), minBusinesses: Number(d.minBusinesses),
        afterRows: Number(d.afterRows), sortOrder: Number(d.sortOrder), active: d.active ? 1 : 0,
      });
      if (resp?.[0]?.[0]?.code === 0) { await load(); flash(`"${d.title}" נשמרה`); }
      else setError(resp?.[0]?.[0]?.msg || 'השמירה נכשלה');
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setBusy(null);
    }
  };

  const itemAction = async (sectionId, businessId, action) => {
    setBusy(`item-${sectionId}-${businessId}-${action}`);
    setError('');
    try {
      const resp = await BOAPI.setFeedSectionItem(sectionId, businessId, action);
      if (resp?.[0]?.[0]?.code === 0) await load();
      else setError(resp?.[0]?.[0]?.msg || 'העדכון נכשל');
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setBusy(null);
    }
  };

  const createSection = async () => {
    setBusy('create');
    setError('');
    try {
      const resp = await BOAPI.saveFeedSection({ title: 'קרוסלה חדשה', icon: 'star-outline', mode: 'manual', maxItems: 10, minBusinesses: 0, afterRows: 6, sortOrder: 10, active: 0 });
      if (resp?.[0]?.[0]?.code === 0) { await load(); flash('נוצרה קרוסלה חדשה (מושבתת) - ערכו, הוסיפו עסקים והפעילו'); }
      else setError(resp?.[0]?.[0]?.msg || 'היצירה נכשלה');
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setBusy(null);
    }
  };

  // למה הקרוסלה לא מוצגת כרגע באפליקציה (או שכן)
  const statusOf = (s, shown) => {
    if (!s.active) return { ok: false, text: 'מושבתת - לא מוצגת באפליקציה' };
    if (s.visibleTotal < s.minBusinesses) return { ok: false, text: `מוסתרת עד שיהיו ${s.minBusinesses} עסקים במועדון (כרגע ${s.visibleTotal})` };
    if (shown.length === 0) return { ok: false, text: 'אין עסקים להצגה - הוסיפו עסקים' };
    return { ok: true, text: `מוצגת באפליקציה · ${shown.length} עסקים` };
  };

  if (loading) return <div style={s.container}><div style={s.center}>טוען...</div></div>;

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <h2 style={s.title}>קרוסלות בעמוד הראשי</h2>
        <button style={s.addBtn} onClick={createSection} disabled={busy === 'create'}>+ קרוסלה חדשה</button>
      </div>
      <p style={s.subtitle}>
        רצועות הלוגואים שמשולבות בין כרטיסי העסקים בעמוד הראשי של האפליקציה. מוצגות רק במצב &quot;הכל&quot; בלי חיפוש וסינון.
        שורת &quot;מבצעים&quot; קבועה אחרי 2 שורות.
      </p>

      {error && <div style={s.errorBanner}>❌ {error} <button style={s.closeErr} onClick={() => setError('')}>✕</button></div>}
      {msg && <div style={s.successBanner}>✅ {msg}</div>}

      {sections.map((sec) => {
        const d = drafts[sec.id] || {};
        const shown = preview.filter((p) => p.sectionId === sec.id);
        const excluded = items.filter((i) => i.sectionId === sec.id && i.kind === 'exclude');
        const includedIds = new Set(items.filter((i) => i.sectionId === sec.id && i.kind === 'include').map((i) => i.businessId));
        const shownIds = new Set(shown.map((p) => p.businessId));
        const addable = businesses.filter((b) => !includedIds.has(b.id) && !shownIds.has(b.id));
        const st = statusOf(sec, shown);
        const dirty = d.title !== sec.title || d.icon !== (sec.icon || '') || d.maxItems !== String(sec.maxItems)
          || d.minBusinesses !== String(sec.minBusinesses) || d.afterRows !== String(sec.afterRows)
          || d.sortOrder !== String(sec.sortOrder) || d.active !== !!sec.active;

        return (
          <div key={sec.id} style={{ ...s.card, ...(sec.active ? {} : s.cardInactive) }}>
            <div style={s.cardHead}>
              <input style={s.titleInput} value={d.title || ''} maxLength={60} onChange={(e) => setDraft(sec.id, { title: e.target.value })} aria-label="כותרת הקרוסלה" />
              <span style={{ ...s.status, ...(st.ok ? s.statusOk : s.statusOff) }}>{st.text}</span>
            </div>
            <div style={s.modeText}>{MODE_LABEL[sec.mode]}</div>

            <div style={s.settings}>
              <label style={s.field}>אייקון
                <select style={s.input} value={d.icon} onChange={(e) => setDraft(sec.id, { icon: e.target.value })}>
                  {ICON_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
              <label style={s.field}>כמה עסקים מוצגים
                <input type="number" min={1} max={30} style={s.input} value={d.maxItems} onChange={(e) => setDraft(sec.id, { maxItems: e.target.value })} />
              </label>
              <label style={s.field}>אחרי כמה שורות עסקים (0 = מעל)
                <input type="number" min={0} style={s.input} value={d.afterRows} onChange={(e) => setDraft(sec.id, { afterRows: e.target.value })} />
              </label>
              <label style={s.field}>להציג מ-X עסקים במועדון
                <input type="number" min={0} style={s.input} value={d.minBusinesses} onChange={(e) => setDraft(sec.id, { minBusinesses: e.target.value })} />
              </label>
              <label style={s.field}>סדר (באותו מיקום)
                <input type="number" style={s.input} value={d.sortOrder} onChange={(e) => setDraft(sec.id, { sortOrder: e.target.value })} />
              </label>
              <label style={{ ...s.field, ...s.checkField }}>
                <input type="checkbox" checked={!!d.active} onChange={(e) => setDraft(sec.id, { active: e.target.checked })} />
                פעילה
              </label>
            </div>
            <div style={s.actionsRow}>
              <button style={{ ...s.saveBtn, ...(dirty ? {} : s.btnDisabled) }} disabled={!dirty || busy === `save-${sec.id}`} onClick={() => save(sec)}>
                {busy === `save-${sec.id}` ? 'שומר...' : 'שמירת הגדרות'}
              </button>
            </div>

            <h4 style={s.subTitle}>מה מוצג עכשיו ({shown.length})</h4>
            {shown.length === 0 ? (
              <div style={s.muted}>{sec.active ? 'אין עסקים בקרוסלה' : 'הקרוסלה מושבתת - התצוגה המקדימה מופיעה אחרי הפעלה'}</div>
            ) : (
              <div style={s.list}>
                {shown.map((p, i) => (
                  <div key={p.businessId} style={s.itemRow}>
                    <span style={s.rank}>{i + 1}</span>
                    {p.logoUrl ? <img src={fullUrl(p.logoUrl)} alt="" style={s.logo} /> : <div style={s.logoPh}>{(p.name || '?')[0]}</div>}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={s.itemName}>{p.name}</div>
                      <div style={s.itemMeta}>{p.pinned ? 'נוסף ידנית' : 'אוטומטי (חדש)'}</div>
                    </div>
                    {p.pinned ? (
                      <>
                        <button style={s.iconBtn} title="למעלה" aria-label={`הזזת ${p.name} למעלה`} onClick={() => itemAction(sec.id, p.businessId, 'up')}>▲</button>
                        <button style={s.iconBtn} title="למטה" aria-label={`הזזת ${p.name} למטה`} onClick={() => itemAction(sec.id, p.businessId, 'down')}>▼</button>
                        <button style={s.removeBtn} onClick={() => itemAction(sec.id, p.businessId, 'reset')}>הסרה</button>
                      </>
                    ) : (
                      <>
                        <button style={s.pinBtn} onClick={() => itemAction(sec.id, p.businessId, 'include')} title="קיבוע בראש הקרוסלה">קיבוע למעלה</button>
                        <button style={s.removeBtn} onClick={() => itemAction(sec.id, p.businessId, 'exclude')}>הסרה</button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={s.addRow}>
              <select style={{ ...s.input, flex: 1 }} value={addPick[sec.id] || ''} onChange={(e) => setAddPick((a) => ({ ...a, [sec.id]: e.target.value }))}>
                <option value="">הוספת בית עסק לקרוסלה...</option>
                {addable.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <button
                style={{ ...s.saveBtn, ...(addPick[sec.id] ? {} : s.btnDisabled) }}
                disabled={!addPick[sec.id]}
                onClick={async () => { await itemAction(sec.id, Number(addPick[sec.id]), 'include'); setAddPick((a) => ({ ...a, [sec.id]: '' })); }}
              >
                הוספה
              </button>
            </div>

            {excluded.length > 0 && (
              <>
                <h4 style={s.subTitle}>הוסרו מהקרוסלה ({excluded.length})</h4>
                <div style={s.chips}>
                  {excluded.map((x) => (
                    <span key={x.businessId} style={s.chip}>
                      {x.name}
                      <button style={s.chipBtn} onClick={() => itemAction(sec.id, x.businessId, 'reset')}>החזרה</button>
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

const s = {
  container:  { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif', maxWidth: 980 },
  headerRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 },
  title:      { margin: 0, color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  subtitle:   { margin: '0 0 18px', color: '#697386', fontSize: 13, lineHeight: 1.6 },
  addBtn:     { padding: '10px 18px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  center:     { padding: 40, textAlign: 'center', color: '#999' },
  errorBanner:{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', color: '#c62828', padding: 12, borderRadius: 8, marginBottom: 14 },
  closeErr:   { background: 'transparent', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 16 },
  successBanner: { background: '#e8f5e9', color: '#2e7d32', padding: 12, borderRadius: 8, marginBottom: 14, fontSize: 13 },
  card:       { background: '#fff', border: '1px solid #e3e8ee', borderRadius: 14, padding: 18, marginBottom: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' },
  cardInactive: { background: '#fafbfc' },
  cardHead:   { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  titleInput: { flex: 1, minWidth: 200, fontSize: 18, fontWeight: 700, padding: '8px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontFamily: 'inherit', color: '#1a1f36' },
  status:     { fontSize: 12, fontWeight: 700, borderRadius: 999, padding: '4px 10px' },
  statusOk:   { background: '#e8f5e9', color: '#2e7d32' },
  statusOff:  { background: '#fff4e5', color: '#b26a00' },
  modeText:   { fontSize: 12.5, color: '#8792a2', margin: '8px 2px 12px' },
  settings:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 10 },
  field:      { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: '#4f566b', fontWeight: 600 },
  checkField: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingTop: 18 },
  input:      { padding: '8px 10px', borderRadius: 8, border: '1.5px solid #e3e8ee', fontSize: 13.5, fontFamily: 'inherit', direction: 'rtl' },
  actionsRow: { display: 'flex', justifyContent: 'flex-end', marginTop: 12 },
  saveBtn:    { padding: '8px 16px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  btnDisabled:{ opacity: 0.4, cursor: 'default' },
  subTitle:   { margin: '16px 0 8px', fontSize: 14, color: '#1a1f36' },
  muted:      { color: '#8792a2', fontSize: 13 },
  list:       { display: 'flex', flexDirection: 'column', gap: 6 },
  itemRow:    { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', borderRadius: 10, background: '#f7f9fc' },
  rank:       { width: 22, textAlign: 'center', fontWeight: 700, color: '#8792a2', fontSize: 13 },
  logo:       { width: 34, height: 34, borderRadius: 17, objectFit: 'cover', border: '1px solid #e3e8ee' },
  logoPh:     { width: 34, height: 34, borderRadius: 17, background: '#EEF2FF', color: '#1f6feb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 },
  itemName:   { fontWeight: 700, color: '#1a1f36', fontSize: 13.5 },
  itemMeta:   { fontSize: 11.5, color: '#8792a2' },
  iconBtn:    { width: 30, height: 28, background: '#fff', border: '1.5px solid #e3e8ee', borderRadius: 7, cursor: 'pointer', fontSize: 11, color: '#4f566b' },
  pinBtn:     { padding: '5px 10px', background: '#EEF2FF', color: '#1f6feb', border: '1px solid #c7d7fb', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  removeBtn:  { padding: '5px 10px', background: '#fff1f1', color: '#d93838', border: '1px solid #fbc4c4', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  addRow:     { display: 'flex', gap: 8, marginTop: 12 },
  chips:      { display: 'flex', flexWrap: 'wrap', gap: 6 },
  chip:       { display: 'inline-flex', alignItems: 'center', gap: 8, background: '#f2f4f7', color: '#4f566b', borderRadius: 999, padding: '4px 6px 4px 12px', fontSize: 12.5, fontWeight: 600 },
  chipBtn:    { background: '#fff', border: '1px solid #e3e8ee', borderRadius: 999, padding: '2px 8px', cursor: 'pointer', fontSize: 11.5, color: '#1f6feb', fontWeight: 700 },
};

export default FeedSectionsTab;
